// pages/application/LeadGenerationPage.js

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SearchInterface from '../../components/lead-generation/core/SearchInterface';
import ESGComplianceFilter from '../../components/lead-generation/business-types/ESGComplianceFilter';
import ESGLeadCard from '../../components/lead-generation/business-types/ESGLeadCard';
import { esgLeadService } from '../../services/lead-generation/ESGLeadGenerationService';
import { useAuth } from '../../contexts/AuthContext';

export default function LeadGenerationPage() {
  const { currentUser, isPaidTier } = useAuth();
  
  // Customer profile type - this will determine which components to show
  const [customerProfile, setCustomerProfile] = useState('esg'); // 'esg', 'local', 'tech', etc.
  
  // State management
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState({
    complianceType: 'all',
    industryRisk: 'all',
    reportingDeadline: 'all',
    country: 'all',
    companySize: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchStats, setSearchStats] = useState(null);

  // Usage tracking for freemium model
  const [usageStats] = useState({
    leadsGenerated: isPaidTier ? 0 : 15,
    leadsLimit: isPaidTier ? 999 : 50,
    apiCallsUsed: 245,
    apiCallsLimit: isPaidTier ? 10000 : 1000
  });

  /**
   * Get page configuration based on customer profile
   */
  const getPageConfig = () => {
    const configs = {
      esg: {
        title: 'Lead Generation - ESG Compliance',
        subtitle: 'VSME & CSRD Requirements',
        icon: 'fas fa-leaf',
        color: 'green',
        placeholder: 'Search for manufacturing companies, ESG software prospects, CSRD compliance...',
        examples: [
          'manufacturing companies london',
          'energy companies germany', 
          'listed companies france',
          'construction firms nottingham'
        ]
      },
      local: {
        title: 'Lead Generation - Local Businesses',
        subtitle: 'Community & Events Focus',
        icon: 'fas fa-store',
        color: 'blue',
        placeholder: 'Search for local businesses, event venues, community organizations...',
        examples: [
          'coffee shops nottingham',
          'restaurants derby',
          'retail stores leicester',
          'professional services'
        ]
      },
      tech: {
        title: 'Lead Generation - Technology',
        subtitle: 'SaaS & Digital Solutions',
        icon: 'fas fa-laptop-code',
        color: 'purple',
        placeholder: 'Search for tech startups, software companies, digital agencies...',
        examples: [
          'tech startups london',
          'software companies manchester',
          'digital agencies birmingham',
          'fintech companies'
        ]
      }
    };
    
    return configs[customerProfile] || configs.esg;
  };

  const pageConfig = getPageConfig();

  /**
   * Handle lead search (routes to appropriate service based on profile)
   */
  const handleSearch = async (searchParams) => {
    setIsSearching(true);
    setHasSearched(true);

    try {
      console.log('Searching leads with params:', searchParams, 'Profile:', customerProfile);
      
      let generatedLeads = [];
      
      // Route to appropriate lead generation service based on customer profile
      switch (customerProfile) {
        case 'esg':
          generatedLeads = await esgLeadService.searchESGLeads(
            {
              query: searchParams.query,
              maxResults: isPaidTier ? 100 : 25
            },
            filters
          );
          break;
          
        case 'local':
          // TODO: Implement local business lead service
          console.log('Local business lead generation not yet implemented');
          generatedLeads = []; // Placeholder
          break;
          
        case 'tech':
          // TODO: Implement tech lead service
          console.log('Tech lead generation not yet implemented');
          generatedLeads = []; // Placeholder
          break;
          
        default:
          generatedLeads = await esgLeadService.searchESGLeads(
            {
              query: searchParams.query,
              maxResults: isPaidTier ? 100 : 25
            },
            filters
          );
      }

      setLeads(generatedLeads);
      setSelectedLeads([]);
      
      // Update search statistics based on profile
      if (customerProfile === 'esg') {
        setSearchStats({
          totalResults: generatedLeads.length,
          csrdMandatory: generatedLeads.filter(l => l.esgAssessment?.primaryRequirement === 'CSRD_MANDATORY').length,
          vsmeEligible: generatedLeads.filter(l => l.esgAssessment?.primaryRequirement === 'VSME_VOLUNTARY').length,
          highPriority: generatedLeads.filter(l => l.priority === 'HIGH' || l.priority === 'CRITICAL').length,
          averageScore: Math.round(generatedLeads.reduce((sum, l) => sum + l.totalScore, 0) / generatedLeads.length) || 0
        });
      } else {
        // Generic stats for other profiles
        setSearchStats({
          totalResults: generatedLeads.length,
          highQuality: generatedLeads.filter(l => l.totalScore >= 80).length,
          mediumQuality: generatedLeads.filter(l => l.totalScore >= 60 && l.totalScore < 80).length,
          averageScore: Math.round(generatedLeads.reduce((sum, l) => sum + l.totalScore, 0) / generatedLeads.length) || 0
        });
      }

    } catch (error) {
      console.error('Lead search failed:', error);
      setLeads([]);
      // Show error message to user
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  /**
   * Handle bulk export
   */
  const handleBulkExport = () => {
    if (selectedLeads.length === 0) return;

    const selectedLeadData = leads.filter(lead => selectedLeads.includes(lead.id));
    
    try {
      let csvData;
      
      // Use appropriate export format based on customer profile
      if (customerProfile === 'esg') {
        csvData = esgLeadService.exportESGLeads(selectedLeadData, 'csv');
      } else {
        // Generic CSV export for other profiles
        csvData = this.convertToGenericCSV(selectedLeadData);
      }
      
      // Create download link
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${customerProfile}_leads_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  /**
   * Render customer profile selector
   */
  const renderProfileSelector = () => (
    <div className="flex items-center space-x-4 mb-6">
      <label className="text-sm font-medium text-gray-700">Customer Type:</label>
      <div className="flex items-center space-x-2">
        {[
          { id: 'esg', name: 'ESG Compliance', icon: 'fas fa-leaf', color: 'green' },
          { id: 'local', name: 'Local Business', icon: 'fas fa-store', color: 'blue' },
          { id: 'tech', name: 'Technology', icon: 'fas fa-laptop-code', color: 'purple' }
        ].map((profile) => (
          <button
            key={profile.id}
            onClick={() => setCustomerProfile(profile.id)}
            className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              customerProfile === profile.id
                ? `bg-${profile.color}-600 text-white shadow-md`
                : `bg-gray-100 text-gray-700 hover:bg-gray-200`
            }`}
          >
            <i className={`${profile.icon} mr-2`} />
            {profile.name}
          </button>
        ))}
      </div>
    </div>
  );

  /**
   * Render appropriate stats based on customer profile
   */
  const renderSearchStats = () => {
    if (!searchStats) return null;
    
    if (customerProfile === 'esg') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{searchStats.totalResults}</div>
            <div className="text-xs text-blue-600 font-medium uppercase tracking-wide">Total Leads</div>
          </div>
          <div className="bg-white border border-red-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{searchStats.csrdMandatory}</div>
            <div className="text-xs text-red-600 font-medium uppercase tracking-wide">CSRD Mandatory</div>
          </div>
          <div className="bg-white border border-yellow-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{searchStats.vsmeEligible}</div>
            <div className="text-xs text-yellow-600 font-medium uppercase tracking-wide">VSME Eligible</div>
          </div>
          <div className="bg-white border border-green-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{searchStats.averageScore}%</div>
            <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Avg Score</div>
          </div>
        </div>
      );
    } else {
      // Generic stats for other profiles
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{searchStats.totalResults}</div>
            <div className="text-xs text-blue-600 font-medium uppercase tracking-wide">Total Leads</div>
          </div>
          <div className="bg-white border border-green-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{searchStats.highQuality || 0}</div>
            <div className="text-xs text-green-600 font-medium uppercase tracking-wide">High Quality</div>
          </div>
          <div className="bg-white border border-yellow-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{searchStats.mediumQuality || 0}</div>
            <div className="text-xs text-yellow-600 font-medium uppercase tracking-wide">Medium Quality</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{searchStats.averageScore}%</div>
            <div className="text-xs text-gray-600 font-medium uppercase tracking-wide">Avg Score</div>
          </div>
        </div>
      );
    }
  };

  /**
   * Render appropriate filters based on customer profile
   */
  const renderFilters = () => {
    if (!showFilters) return null;
    
    switch (customerProfile) {
      case 'esg':
        return (
          <ESGComplianceFilter
            onFilterChange={handleFilterChange}
            currentFilters={filters}
          />
        );
      case 'local':
        // TODO: Implement LocalBusinessFilter
        return <div className="bg-white p-4 rounded-lg">Local business filters coming soon...</div>;
      case 'tech':
        // TODO: Implement TechStartupFilter
        return <div className="bg-white p-4 rounded-lg">Tech filters coming soon...</div>;
      default:
        return (
          <ESGComplianceFilter
            onFilterChange={handleFilterChange}
            currentFilters={filters}
          />
        );
    }
  };

  /**
   * Render appropriate lead cards based on customer profile
   */
  const renderLeadCard = (lead) => {
    switch (customerProfile) {
      case 'esg':
        return (
          <ESGLeadCard
            key={lead.id}
            lead={lead}
            isSelected={selectedLeads.includes(lead.id)}
            onSelect={(id) => {
              if (selectedLeads.includes(id)) {
                setSelectedLeads(selectedLeads.filter(selectedId => selectedId !== id));
              } else {
                setSelectedLeads([...selectedLeads, id]);
              }
            }}
          />
        );
      case 'local':
        // TODO: Implement LocalBusinessLeadCard
        return (
          <div key={lead.id} className="bg-white p-4 rounded-lg border">
            Local business card for: {lead.name}
          </div>
        );
      case 'tech':
        // TODO: Implement TechLeadCard
        return (
          <div key={lead.id} className="bg-white p-4 rounded-lg border">
            Tech lead card for: {lead.name}
          </div>
        );
      default:
        return (
          <ESGLeadCard
            key={lead.id}
            lead={lead}
            isSelected={selectedLeads.includes(lead.id)}
            onSelect={(id) => {
              if (selectedLeads.includes(id)) {
                setSelectedLeads(selectedLeads.filter(selectedId => selectedId !== id));
              } else {
                setSelectedLeads([...selectedLeads, id]);
              }
            }}
          />
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        
        {/* Header Section */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{pageConfig.title}</h1>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-1">
                {pageConfig.subtitle}
              </p>
            </div>
            
            {/* Usage Stats */}
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {usageStats.leadsGenerated}/{usageStats.leadsLimit}
                </div>
                <div className="text-xs text-gray-500">Leads this month</div>
              </div>
              <div className="w-16 h-2 bg-gray-200 rounded-full">
                <div
                  className={`h-2 bg-${pageConfig.color}-600 rounded-full transition-all duration-300`}
                  style={{ width: `${(usageStats.leadsGenerated / usageStats.leadsLimit) * 100}%` }}
                />
              </div>
              {!isPaidTier && (
                <button className={`px-4 py-2 bg-gradient-to-r from-${pageConfig.color}-600 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-${pageConfig.color}-700 hover:to-emerald-700 transition-all`}>
                  Upgrade for More
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Customer Profile Selector */}
        {renderProfileSelector()}

        {/* Search Stats */}
        {renderSearchStats()}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          
          {/* Search Interface - Full Width */}
          <div className="xl:col-span-4">
            <SearchInterface
              onSearch={handleSearch}
              onToggleFilters={() => setShowFilters(!showFilters)}
              isSearching={isSearching}
              hasFilters={Object.values(filters).some(f => f !== 'all')}
              placeholder={pageConfig.placeholder}
              searchExamples={pageConfig.examples}
            />
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="xl:col-span-1">
              {renderFilters()}
            </div>
          )}

          {/* Results Area */}
          <div className={`${showFilters ? 'xl:col-span-3' : 'xl:col-span-4'}`}>
            {/* Loading State */}
            {isSearching && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
                <div className="text-center">
                  <div className={`animate-spin rounded-full h-12 w-12 border-b-2 border-${pageConfig.color}-600 mx-auto mb-4`} />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Searching for {customerProfile} leads...
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Analyzing potential customers in your target market
                  </p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isSearching && !hasSearched && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
                <div className="text-center">
                  <div className={`w-16 h-16 bg-${pageConfig.color}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <i className={`${pageConfig.icon} text-${pageConfig.color}-600 text-2xl`} />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Find Your Next Customers
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Use our AI-powered lead generation to discover potential customers in your target market.
                  </p>
                </div>
              </div>
            )}

            {/* No Results */}
            {hasSearched && leads.length === 0 && !isSearching && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
                  <p className="text-gray-600 mb-4">
                    Try adjusting your search terms or filters to find more prospects.
                  </p>
                </div>
              </div>
            )}

            {/* Results */}
            {leads.length > 0 && (
              <div className="space-y-4">
                {/* Bulk Actions Header */}
                {selectedLeads.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {selectedLeads.length} leads selected
                      </span>
                      <button
                        onClick={handleBulkExport}
                        className={`flex items-center px-3 py-2 bg-${pageConfig.color}-600 text-white text-sm font-medium rounded-lg hover:bg-${pageConfig.color}-700 transition-all`}
                      >
                        <i className="fas fa-download mr-2" />
                        Export Leads
                      </button>
                    </div>
                  </div>
                )}

                {/* Leads Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {leads.map(lead => renderLeadCard(lead))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

/* 
Environment Variable Setup:

For React applications (client-side), you need to set up environment variables in the CLIENT:

1. Create a .env file in your PROJECT ROOT (same level as package.json):

```
# .env file (client-side environment variables)
REACT_APP_COMPANIES_HOUSE_API_KEY=your_companies_house_api_key_here
REACT_APP_ENVIRONMENT=development
```

2. For production, set environment variables in your hosting platform:
   - Vercel: Project Settings → Environment Variables
   - Netlify: Site Settings → Environment Variables
   - AWS Amplify: App Settings → Environment Variables

Important Notes:
- Environment variables in React MUST start with "REACT_APP_"
- These are CLIENT-SIDE variables (visible in browser)
- Never put sensitive server secrets in client environment variables
- The Companies House API key is safe for client-side use (it's designed for public access)
- Variables are built into the JavaScript bundle at build time

For sensitive server-side operations (if you add a backend later):
- Use regular environment variables without REACT_APP_ prefix
- Store in server environment or .env files that aren't committed to git
- Use services like AWS Secrets Manager, Azure Key Vault, etc. for production
*/